/**
 * Analytics e coleta de leads — a Etapa 7 do planejamento, do lado do navegador.
 *
 * Dois canais, deliberadamente separados:
 *
 * - **Analytics** (`/events`): quantas conversas começaram, quantas terminaram e **em qual bloco a
 *   pessoa desistiu**. Só ids de bloco e contadores. Nenhum valor digitado pelo visitante passa por
 *   aqui — misturar as duas coisas transformaria um painel de números em um vazamento de dado
 *   pessoal.
 * - **Leads** (`/leads`): as variáveis coletadas, uma vez, quando a conversa termina. É o dado
 *   pessoal, e por isso é uma requisição explícita, condicionada à configuração do webchat.
 *
 * O abandono é o número mais difícil de obter e o mais útil: ele acontece quando **nada** acontece.
 * A saída é escutar `pagehide`/`visibilitychange` e mandar o evento com `sendBeacon`, que o navegador
 * entrega mesmo com a aba fechando — `fetch` comum seria cancelado no meio.
 */

import type { TranscriptEntry, Variables } from '@merlin/flow-engine';
import { SDK_VERSION } from '../version.js';

export type TelemetryEventType = 'start' | 'node' | 'complete' | 'abandon';

export interface TelemetryEvent {
  type: TelemetryEventType;
  nodeId?: string;
}

export interface TelemetryOptions {
  apiBase: string;
  publicKey: string;
  sessionId: string;
  /** Falso no preview do painel: número de teste não deve virar métrica de produção. */
  enabled: boolean;
  /** Espera antes de despachar o lote, para uma conversa rápida não gerar uma requisição por bloco. */
  flushDelayMs?: number;
}

export interface Telemetry {
  start(): void;
  node(nodeId: string): void;
  complete(): void;
  submitLead(variables: Variables, transcript: TranscriptEntry[]): void;
  destroy(): void;
}

const DEFAULT_FLUSH_DELAY_MS = 2500;

function noop(): Telemetry {
  return {
    start: () => {},
    node: () => {},
    complete: () => {},
    submitLead: () => {},
    destroy: () => {},
  };
}

export function createTelemetry(options: TelemetryOptions): Telemetry {
  if (!options.enabled || !options.apiBase || !options.publicKey) return noop();

  const eventsUrl = `${options.apiBase.replace(/\/$/, '')}/events?key=${encodeURIComponent(options.publicKey)}`;
  const leadsUrl = `${options.apiBase.replace(/\/$/, '')}/leads?key=${encodeURIComponent(options.publicKey)}`;
  const flushDelay = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS;

  let pending: TelemetryEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let started = false;
  let finished = false;
  let lastNodeId: string | null = null;
  let destroyed = false;

  function send(body: unknown, useBeacon: boolean): void {
    const payload = JSON.stringify(body);

    // `sendBeacon` é o único caminho confiável com a aba fechando; fora disso, `fetch` normal.
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(eventsUrl, blob)) return;
      } catch {
        // cai no fetch abaixo
      }
    }

    void fetch(eventsUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      credentials: 'omit',
      mode: 'cors',
      keepalive: useBeacon,
    }).catch(() => {
      // Métrica não é crítica: falhar aqui não pode atrapalhar a conversa.
    });
  }

  function flush(useBeacon = false): void {
    if (pending.length === 0) return;
    const events = pending;
    pending = [];
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    send({ sessionId: options.sessionId, sdkVersion: SDK_VERSION, events }, useBeacon);
  }

  function enqueue(event: TelemetryEvent, immediate = false): void {
    if (destroyed) return;
    pending.push(event);
    if (immediate) {
      flush();
      return;
    }
    if (!timer) timer = setTimeout(() => flush(), flushDelay);
  }

  /*
   * Abandono: a conversa começou, não terminou, e a página está saindo. `visibilitychange` cobre o
   * caso de trocar de aba no celular, que é onde `pagehide` costuma não disparar.
   */
  function onLeaving(): void {
    if (!started || finished) return;
    finished = true;
    enqueue({ type: 'abandon', ...(lastNodeId ? { nodeId: lastNodeId } : {}) });
    flush(true);
  }

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') onLeaving();
  };

  window.addEventListener('pagehide', onLeaving);
  document.addEventListener('visibilitychange', onVisibility);

  return {
    start(): void {
      if (started) return;
      started = true;
      finished = false;
      enqueue({ type: 'start' });
    },

    node(nodeId: string): void {
      // Vários efeitos saem do mesmo bloco (mensagem + pergunta, por exemplo); só a transição conta.
      if (!nodeId || nodeId === lastNodeId) return;
      lastNodeId = nodeId;
      enqueue({ type: 'node', nodeId });
    },

    complete(): void {
      if (finished) return;
      finished = true;
      enqueue({ type: 'complete' }, true);
    },

    submitLead(variables: Variables, transcript: TranscriptEntry[]): void {
      if (destroyed) return;
      if (!variables || Object.keys(variables).length === 0) return;

      void fetch(leadsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: options.sessionId,
          sdkVersion: SDK_VERSION,
          variables,
          // Só as falas, sem estrutura interna: é o que dá contexto a quem for atender.
          transcript: transcript.map((entry) => ({ role: entry.role, text: entry.text })),
          pageUrl: location.href,
        }),
        credentials: 'omit',
        mode: 'cors',
        keepalive: true,
      }).catch(() => {
        /* silencioso: perder um lead não pode quebrar a conversa do visitante */
      });
    },

    destroy(): void {
      destroyed = true;
      flush();
      window.removeEventListener('pagehide', onLeaving);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
